<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">{{ workspaceTitle }}</h1>
        <p class="console-page__placeholder">
          <span class="console-badge console-badge--small" :class="`console-badge--${workspaceStatusTone}`">{{ workspace.status || 'unknown' }}</span>
          <span v-if="workspaceSourceLabel" class="workspace-detail__stat">{{ workspaceSourceLabel }}</span>
          <span v-if="workspace.hasCredentials" class="workspace-detail__stat">Credentials attached</span>
          <span class="workspace-detail__stat">{{ formatRelativeTime(workspace.createdAt) }}</span>
        </p>
        <ConsolePollingStatus
          :active="workspacePollingActive"
          :busy="pollBusy"
          idle-label="Watching workspace status every 3s"
          busy-label="Refreshing workspace status..."
          tone="pending"
        />
      </div>
      <div class="console-page__actions">
        <button type="button" class="button button--ghost" :disabled="loading || actionBusy" @click="loadData({ showLoading: true })">Refresh</button>
        <router-link :to="{ name: 'ConsoleJobCreate', query: { workspaceId } }" class="button button--primary">Submit Request</router-link>
      </div>
    </div>

    <section class="console-panel" :class="`console-panel--${workspaceStatusTone}`">
      <ConsoleListState :loading="loading" :error="error" :is-empty="false" loading-message="Loading workspace...">
        <div v-if="warnings.length" class="workspace-detail__warnings">
          <div v-for="warning in warnings" :key="warning" class="alert alert--error">{{ warning }}</div>
        </div>

        <div v-if="staleness" class="workspace-detail__action-block">
          <p><strong>Workspace is stale.</strong> Behind {{ staleness.targetBranch }} by {{ staleness.behindByCommits }} commits.</p>
          <p v-if="staleApprovalContext" class="workspace-detail__action-note">
            Approval for {{ staleApprovalSourceLabel }} run <span class="workspace-detail__mono">{{ staleApprovalJobId }}</span> paused because this workspace is behind its target branch. Choose how to continue.
          </p>
          <div class="button-row">
            <button v-if="canContinueStaleApproval" type="button" class="button button--ghost" :disabled="actionBusy" @click="submitStalenessDecision('continue')">{{ actionBusy ? 'Working...' : 'Continue Anyway' }}</button>
            <button v-if="staleness.canSyncInPlace" type="button" class="button button--primary" :disabled="actionBusy" @click="submitStalenessDecision('sync')">{{ actionBusy ? 'Working...' : 'Sync Workspace' }}</button>
            <button type="button" class="button button--ghost" :disabled="actionBusy" @click="submitStalenessDecision('new-workspace')">{{ actionBusy ? 'Working...' : 'Create Fresh Workspace' }}</button>
          </div>
        </div>

        <details class="console-module" open>
          <summary class="console-module__summary">Overview</summary>
          <div class="workspace-detail__overview">
            <div class="workspace-detail__overview-main">
              <p v-if="workspace.description" class="workspace-detail__description">{{ workspace.description }}</p>
              <p v-else class="workspace-detail__description workspace-detail__description--placeholder">No description is stored for this workspace yet.</p>
              <p v-if="workspace.repoUrl" class="workspace-detail__repo">
                <a :href="workspace.repoUrl" target="_blank" rel="noopener" class="external-link">{{ workspace.repoUrl }}</a>
                <span v-if="workspace.branch"> @ {{ workspace.branch }}</span>
              </p>
            </div>
            <div class="console-signal-strip workspace-detail__metrics-grid">
              <div class="console-signal console-signal--ready">
                <span class="console-signal__label">Total Runs</span>
                <span class="console-signal__value">{{ jobs.length }}</span>
                <span class="console-signal__note">Runs currently attached to this workspace.</span>
              </div>
              <div class="console-signal" :class="activeJobs > 0 ? 'console-signal--active' : 'console-signal--pending'">
                <span class="console-signal__label">Active</span>
                <span class="console-signal__value">{{ activeJobs }}</span>
                <span class="console-signal__note">Running or assembling work in this workspace.</span>
              </div>
              <div class="console-signal" :class="failedJobs > 0 ? 'console-signal--failed' : 'console-signal--ready'">
                <span class="console-signal__label">Failed</span>
                <span class="console-signal__value">{{ failedJobs }}</span>
                <span class="console-signal__note">Recent failures that may need follow-up.</span>
              </div>
            </div>
          </div>
        </details>

        <details class="console-module" :open="editingName || editingDescription || showTokenRefresh || Boolean(refreshSuccess) || Boolean(refreshError)">
          <summary class="console-module__summary">Administration</summary>
          <div class="workspace-detail__admin-grid">
            <section class="workspace-detail__admin-block">
              <div class="workspace-detail__admin-header">
                <div>
                  <h2 class="workspace-detail__admin-title">Workspace Name</h2>
                  <p class="workspace-detail__admin-note">Operator-facing name used in lists and overview surfaces.</p>
                </div>
                <button type="button" class="button button--ghost" :disabled="metadataBusy" @click="editingName ? cancelNameEdit() : openNameEditor()">{{ editingName ? 'Cancel' : 'Edit Name' }}</button>
              </div>
              <form v-if="editingName" class="workspace-detail__admin-form" @submit.prevent="saveName">
                <input v-model="editNameValue" type="text" class="form-input workspace-detail__input" placeholder="Main Workspace" />
                <div class="button-row">
                  <button type="submit" class="button button--primary" :disabled="metadataBusy">{{ metadataBusy ? 'Saving...' : 'Save Name' }}</button>
                  <button type="button" class="button button--ghost" :disabled="metadataBusy" @click="cancelNameEdit">Cancel</button>
                </div>
              </form>
              <p v-else class="workspace-detail__admin-value">{{ workspaceTitle }}</p>
            </section>

            <section class="workspace-detail__admin-block">
              <div class="workspace-detail__admin-header">
                <div>
                  <h2 class="workspace-detail__admin-title">Description</h2>
                  <p class="workspace-detail__admin-note">Short repository context for operators launching work.</p>
                </div>
                <button type="button" class="button button--ghost" :disabled="metadataBusy" @click="editingDescription ? cancelDescriptionEdit() : openDescriptionEditor()">{{ editingDescription ? 'Cancel' : 'Edit Description' }}</button>
              </div>
              <form v-if="editingDescription" class="workspace-detail__admin-form" @submit.prevent="saveDescription">
                <textarea v-model="editDescriptionValue" rows="4" class="form-input workspace-detail__textarea" placeholder="Primary repository context" />
                <div class="button-row">
                  <button type="submit" class="button button--primary" :disabled="metadataBusy">{{ metadataBusy ? 'Saving...' : 'Save Description' }}</button>
                  <button type="button" class="button button--ghost" :disabled="metadataBusy" @click="cancelDescriptionEdit">Cancel</button>
                </div>
              </form>
              <p v-else class="workspace-detail__admin-value" :class="{ 'workspace-detail__admin-value--placeholder': !workspace.description }">{{ workspace.description || 'No description stored.' }}</p>
            </section>

            <section v-if="canManageCredentials" class="workspace-detail__admin-block">
              <div class="workspace-detail__admin-header">
                <div>
                  <h2 class="workspace-detail__admin-title">GitHub Credentials</h2>
                  <p class="workspace-detail__admin-note">Add or update one or more credential fields. The runtime uses GitHub PAT first, then SSH key (with OAuth token as the REST API accessory). Resubmit a value (even unchanged) to retry paused jobs after granting permission server-side.</p>
                </div>
                <button type="button" class="button button--ghost" :disabled="refreshBusy" @click="toggleTokenRefresh">{{ showTokenRefresh ? 'Cancel' : (workspace.hasCredentials ? 'Update Credentials' : 'Add Credentials') }}</button>
              </div>
              <p class="workspace-detail__admin-value">{{ workspace.hasCredentials ? 'GitHub credentials are currently attached to this workspace.' : 'No GitHub credentials attached. Add a PAT or SSH key so runs that depend on git authentication can succeed.' }}</p>
              <form v-if="showTokenRefresh" class="workspace-detail__admin-form" @submit.prevent="refreshCredentials">
                <label class="workspace-detail__admin-field">
                  <span class="workspace-detail__admin-field-label">GitHub Personal Access Token (PAT)</span>
                  <input v-model="refreshPatValue" type="password" class="form-input workspace-detail__input workspace-detail__input--mono" placeholder="ghp_... or github_pat_..." autocomplete="off" />
                </label>
                <label class="workspace-detail__admin-field">
                  <span class="workspace-detail__admin-field-label">SSH Private Key</span>
                  <textarea v-model="refreshSshKeyValue" rows="3" class="form-input workspace-detail__textarea workspace-detail__input--mono" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" autocomplete="off" />
                </label>
                <label class="workspace-detail__admin-field">
                  <span class="workspace-detail__admin-field-label">GitHub OAuth Token (REST API for SSH workspaces)</span>
                  <input v-model="refreshOauthTokenValue" type="password" class="form-input workspace-detail__input workspace-detail__input--mono" placeholder="gho_..." autocomplete="off" />
                </label>
                <div class="button-row">
                  <button type="submit" class="button button--primary" :disabled="refreshBusy || !anyCredentialFieldFilled">{{ refreshBusy ? 'Updating...' : 'Update Credentials' }}</button>
                  <button type="button" class="button button--ghost" :disabled="refreshBusy" @click="toggleTokenRefresh">Cancel</button>
                </div>
              </form>
              <p v-if="refreshSuccess" class="workspace-detail__admin-message">{{ refreshSuccess }}</p>
              <p v-if="refreshError" class="workspace-detail__admin-error">{{ refreshError }}</p>
            </section>
          </div>
          <p v-if="metadataMessage" class="workspace-detail__admin-message">{{ metadataMessage }}</p>
          <p v-if="metadataError" class="workspace-detail__admin-error">{{ metadataError }}</p>
        </details>

        <details v-if="!jobsLoading || jobs.length" class="console-module" :open="jobs.length > 0 && jobs.length <= 5">
          <summary class="console-module__summary">Recent Runs <span class="console-module__count">{{ jobs.length }}</span></summary>
          <div v-if="jobs.length" class="console-stream workspace-detail__job-list">
            <router-link v-for="job in jobs.slice(0, 10)" :key="job.jobId" :to="{ name: 'ConsoleJobDetail', params: { id: job.jobId } }" class="console-stream__item">
              <div>
                <p class="console-stream__title">{{ summarizeJobTitle(job.description, job.jobId, 60) }}</p>
                <p class="console-stream__meta">{{ formatRelativeTime(job.updatedAt) }}</p>
              </div>
              <div class="console-stream__aside">
                <span class="console-badge console-badge--small" :class="`console-badge--${job.tone}`">{{ job.statusLabel }}</span>
              </div>
            </router-link>
          </div>
          <p v-else class="workspace-detail__empty">No runs for this workspace yet.</p>
          <router-link v-if="jobs.length > 10" :to="{ name: 'ConsoleJobs', query: { workspaceId } }" class="workspace-detail__view-all">View all {{ jobs.length }} runs</router-link>
        </details>

        <details v-if="!analyticsLoading || analytics" class="console-module" :open="Boolean(recurringTopics.length || gapTypeMetrics.length)">
          <summary class="console-module__summary">Guidance Gaps <span v-if="analytics" class="console-module__count">{{ analytics.guidance_gaps.total_gaps }} gaps</span></summary>
          <div v-if="analytics">
            <div v-if="gapTypeMetrics.length" class="workspace-detail__gap-tags">
              <span v-for="item in gapTypeMetrics" :key="item.label" class="console-badge console-badge--neutral">{{ item.label }}: {{ item.value }}</span>
            </div>
            <div v-if="recurringTopics.length" class="workspace-detail__topic-list">
              <div v-for="topic in recurringTopics" :key="topic.topic" class="workspace-detail__topic">
                <div class="workspace-detail__topic-header">
                  <span class="workspace-detail__topic-title">{{ topic.topic }}</span>
                  <span class="workspace-detail__topic-count">{{ topic.count }}x</span>
                </div>
                <div class="workspace-detail__topic-types">
                  <span v-for="gapType in topic.gap_types" :key="gapType" class="console-badge console-badge--small console-badge--neutral">{{ formatJobStatus(gapType) }}</span>
                </div>
              </div>
            </div>
            <p v-else class="workspace-detail__empty">No recurring guidance gaps.</p>
          </div>
          <p v-else class="workspace-detail__empty">Analytics not available.</p>
          <router-link :to="{ name: 'ConsoleAnalytics', query: { workspaceId } }" class="workspace-detail__view-all">Open full analytics</router-link>
        </details>

        <details v-if="analytics" class="console-module">
          <summary class="console-module__summary">Analytics Scope</summary>
          <dl class="workspace-detail__meta-list">
            <div><dt>Runs with analytics</dt><dd>{{ analytics.scope.jobs_with_analytics }}</dd></div>
            <div><dt>Runs with gaps</dt><dd>{{ analytics.guidance_gaps.jobs_with_gaps }}</dd></div>
            <div><dt>Total mismatches</dt><dd>{{ analytics.mismatch_hotspots.total_mismatches }}</dd></div>
            <div><dt>Runtime-evidenced runs</dt><dd>{{ analytics.evidence_coverage.jobs_with_runtime_evidence }}</dd></div>
          </dl>
        </details>

        <details class="console-module">
          <summary class="console-module__summary">Details</summary>
          <dl class="workspace-detail__meta-list">
            <div><dt>Workspace ID</dt><dd class="workspace-detail__mono">{{ workspaceId }}</dd></div>
            <div><dt>Status</dt><dd>{{ workspace.status || 'unknown' }}</dd></div>
            <div><dt>Created</dt><dd>{{ formatRelativeTime(workspace.createdAt) }}</dd></div>
            <div v-if="workspace.lastSyncedAt"><dt>Last synced</dt><dd>{{ formatRelativeTime(workspace.lastSyncedAt) }}</dd></div>
            <div v-if="workspace.repoUrl"><dt>Repository</dt><dd class="workspace-detail__mono">{{ workspace.repoUrl }}</dd></div>
            <div v-if="workspace.branch"><dt>Branch</dt><dd class="workspace-detail__mono">{{ workspace.branch }}</dd></div>
            <div v-if="workspace.failureReason"><dt>Failure</dt><dd>{{ workspace.failureReason }}</dd></div>
          </dl>
          <div v-if="sourceRecords.length" class="workspace-detail__source-block">
            <p class="workspace-detail__source-heading">Sources</p>
            <ul class="workspace-detail__source-list">
              <li v-for="(source, index) in sourceRecords" :key="`${source.type || 'source'}-${index}`"><span class="workspace-detail__mono">{{ formatSourceSummary(source) }}</span></li>
            </ul>
          </div>
          <div class="workspace-detail__actions">
            <button v-if="workspace.status !== 'archived'" type="button" class="button button--ghost" :disabled="actionBusy" @click="updateWorkspace({ status: 'archived' }, 'Workspace archived.')">{{ actionBusy ? 'Working...' : 'Archive' }}</button>
            <button v-else type="button" class="button button--ghost" :disabled="actionBusy" @click="updateWorkspace({ status: 'ready' }, 'Workspace restored.')">{{ actionBusy ? 'Working...' : 'Restore' }}</button>
            <button type="button" class="button button--ghost workspace-detail__delete" :disabled="actionBusy" @click="prepareDeleteWorkspace">{{ actionBusy ? 'Working...' : 'Delete Workspace' }}</button>
          </div>
          <div v-if="deleteConfirmOpen" class="workspace-detail__delete-confirm" role="alert">
            <h3 class="workspace-detail__delete-title">Confirm workspace deletion</h3>
            <p>Deleting this workspace permanently removes workspace metadata and deletes workspace-owned remote branches.</p>
            <ul v-if="deleteImpactBranches.length" class="workspace-detail__delete-branch-list">
              <li v-for="branch in deleteImpactBranches" :key="`${branch.source}-${branch.name}`">
                <span class="workspace-detail__mono">{{ branch.name }}</span>
                <span class="workspace-detail__delete-source">{{ branch.source === 'job' ? `Run branch${branch.jobId ? `: ${branch.jobId}` : ''}` : 'Workspace branch' }}</span>
              </li>
            </ul>
            <p v-else class="workspace-detail__empty">No workspace-owned remote branches are recorded for cleanup.</p>
            <div v-if="deleteImpactWarnings.length" class="workspace-detail__delete-warnings">
              <p v-for="warning in deleteImpactWarnings" :key="warning">{{ warning }}</p>
            </div>
            <p v-if="deleteImpactError" class="workspace-detail__admin-error">{{ deleteImpactError }}</p>
            <div class="button-row">
              <button type="button" class="button button--ghost workspace-detail__delete" :disabled="actionBusy" @click="confirmDeleteWorkspace">{{ actionBusy ? 'Deleting...' : 'Confirm Delete' }}</button>
              <button type="button" class="button button--ghost" :disabled="actionBusy" @click="cancelDeleteWorkspace">Cancel</button>
            </div>
          </div>
        </details>

        <p v-if="actionMessage" class="workspace-detail__message">{{ actionMessage }}</p>
        <router-link v-if="deleteCompletedWithWarnings" :to="{ name: 'ConsoleWorkspaces' }" class="workspace-detail__view-all">Return to Workspaces</router-link>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ConsoleListState from '@/components/ConsoleListState.vue'
import ConsolePollingStatus from '@/components/ConsolePollingStatus.vue'
import { formatJobStatus, isActiveJobStatus, normalizeJob, summarizeJobTitle } from '@/composables/useJobPresentation.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { formatRelativeTime } from '@/composables/useRelativeTime.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { appWorkspaceTaskClient } from '@/services/WorkspaceTaskClient.js'
import { fetchAllJobSummaryItems } from '@/services/jobSummary.js'

const route = useRoute()
const router = useRouter()
const { request, requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsoleWorkspaceDetail]')

const POLL_INTERVAL_MS = 3000
const MAX_POLL_ERRORS = 5

const workspaceId = route.params.id

const loading = ref(true)
const error = ref('')
const warnings = ref([])
const workspace = ref({})
const jobs = ref([])
const jobsLoading = ref(false)
const analytics = ref(null)
const analyticsLoading = ref(false)
const staleness = ref(null)
const stalenessLoading = ref(false)
const actionBusy = ref(false)
const actionMessage = ref('')
const deleteConfirmOpen = ref(false)
const deleteImpact = ref(null)
const deleteImpactError = ref('')
const deleteCompletedWithWarnings = ref(false)

const metadataBusy = ref(false)
const metadataMessage = ref('')
const metadataError = ref('')
const editingName = ref(false)
const editNameValue = ref('')
const editingDescription = ref(false)
const editDescriptionValue = ref('')
const pollBusy = ref(false)

const showTokenRefresh = ref(false)
const refreshPatValue = ref('')
const refreshSshKeyValue = ref('')
const refreshOauthTokenValue = ref('')
const refreshBusy = ref(false)
const refreshError = ref('')
const refreshSuccess = ref('')

const anyCredentialFieldFilled = computed(() => {
  return refreshPatValue.value.trim().length > 0
    || refreshSshKeyValue.value.trim().length > 0
    || refreshOauthTokenValue.value.trim().length > 0
})

let pollTimer = null
let pollErrorCount = 0

function normalizeWorkspace(data) {
  return {
    ...data,
    workspaceId: data.workspaceId ?? data.id,
  }
}

function addWarning(message) {
  if (!warnings.value.includes(message)) {
    warnings.value.push(message)
  }
}

function isPendingWorkspaceStatus(status) {
  return status === 'creating'
}

const sourceRecords = computed(() => (Array.isArray(workspace.value.sources) ? workspace.value.sources : []))

const hasGitHubSource = computed(() => {
  const type = workspace.value.type
  if (type === 'public-github' || type === 'private-github') return true
  return sourceRecords.value.some((source) => source.type === 'github')
})

// The credentials form is available for any GitHub-source workspace,
// whether or not credentials are already attached. The backend's preflight
// surfaces credential failures even for workspaces with no stored
// credentials (a push attempted with `type: 'none'` is classified as a
// recoverable credential failure), so the operator needs the same UI to
// ADD a PAT or SSH key as they do to UPDATE an existing one.
const canManageCredentials = computed(() => hasGitHubSource.value)
const staleApprovalReason = computed(() => typeof route.query.reason === 'string' ? route.query.reason : '')
const staleApprovalSource = computed(() => typeof route.query.source === 'string' ? route.query.source : '')
const staleApprovalJobId = computed(() => typeof route.query.jobId === 'string' ? route.query.jobId : '')
const staleApprovalContext = computed(() => staleApprovalReason.value === 'stale-approval' && Boolean(staleApprovalJobId.value))
const canContinueStaleApproval = computed(() => staleApprovalContext.value && Boolean(staleness.value))

const workspaceTitle = computed(() => workspace.value.name?.trim() || workspaceId)
const staleApprovalSourceLabel = computed(() => staleApprovalSource.value === 'plan' ? 'plan' : 'run')
const workspacePollingActive = computed(() => pollBusy.value || isPendingWorkspaceStatus(workspace.value.status))

const workspaceSourceLabel = computed(() => {
  if (hasGitHubSource.value) return 'GitHub'
  if (workspace.value.type) return workspace.value.type.replace(/[-_]/g, ' ')
  return ''
})

const workspaceStatusTone = computed(() => {
  const map = {
    ready: 'ready',
    creating: 'pending',
    archived: 'neutral',
    failed: 'failed',
  }
  return map[workspace.value.status] ?? 'neutral'
})

const failedJobs = computed(() => jobs.value.filter((job) => job.status === 'failed').length)
const activeJobs = computed(() => jobs.value.filter((job) => isActiveJobStatus(job.status)).length)
const recurringTopics = computed(() => analytics.value?.guidance_gaps?.recurring_topics ?? [])
const deleteImpactBranches = computed(() => Array.isArray(deleteImpact.value?.branches) ? deleteImpact.value.branches : [])
const deleteImpactWarnings = computed(() => Array.isArray(deleteImpact.value?.warnings) ? deleteImpact.value.warnings : [])

const gapTypeMetrics = computed(() => {
  const byType = analytics.value?.guidance_gaps?.by_type
  if (!byType) return []
  return Object.entries(byType)
    .map(([label, value]) => ({ label: formatJobStatus(label), value }))
    .filter((item) => item.value > 0)
})

function resetMetadataState() {
  metadataMessage.value = ''
  metadataError.value = ''
}

function resetRefreshState() {
  refreshError.value = ''
  refreshSuccess.value = ''
}

function resetDeleteState() {
  deleteConfirmOpen.value = false
  deleteImpact.value = null
  deleteImpactError.value = ''
  deleteCompletedWithWarnings.value = false
}

function applyWorkspace(data) {
  workspace.value = normalizeWorkspace(data)
}

function stopWorkspacePolling() {
  if (pollTimer !== null) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
  pollErrorCount = 0
}

function scheduleWorkspacePoll() {
  if (pollTimer !== null) return

  pollTimer = setTimeout(async () => {
    pollTimer = null
    await pollWorkspaceStatus()
  }, POLL_INTERVAL_MS)
}

function formatSourceSummary(source) {
  if (!source || typeof source !== 'object') return 'Unknown source'
  if (source.type === 'github') {
    const repo = source.repoUrl || source.repoName || 'GitHub source'
    return source.branch ? `${repo} @ ${source.branch}` : repo
  }
  return source.s3Uri || source.repoName || source.type || 'Source'
}

async function patchWorkspace(patch) {
  const data = await requestJson(`/workspaces/${workspaceId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  applyWorkspace(data)
  return data
}

function openNameEditor() {
  resetMetadataState()
  editNameValue.value = workspace.value.name ?? ''
  editingName.value = true
}

function cancelNameEdit() {
  editingName.value = false
  editNameValue.value = ''
  resetMetadataState()
}

async function saveName() {
  if (metadataBusy.value) return
  const name = editNameValue.value.trim()
  if (!name) {
    metadataError.value = 'Name cannot be empty.'
    return
  }
  metadataBusy.value = true
  resetMetadataState()
  try {
    await patchWorkspace({ name })
    editingName.value = false
    metadataMessage.value = 'Workspace name updated.'
  } catch (saveError) {
    metadataError.value = `Failed to update name: ${saveError.message}`
    logger.warn('Failed to update workspace name', saveError)
  } finally {
    metadataBusy.value = false
  }
}

function openDescriptionEditor() {
  resetMetadataState()
  editDescriptionValue.value = workspace.value.description ?? ''
  editingDescription.value = true
}

function cancelDescriptionEdit() {
  editingDescription.value = false
  editDescriptionValue.value = ''
  resetMetadataState()
}

async function saveDescription() {
  if (metadataBusy.value) return
  const description = editDescriptionValue.value.trim()
  metadataBusy.value = true
  resetMetadataState()
  try {
    await patchWorkspace({ description })
    editingDescription.value = false
    metadataMessage.value = description ? 'Workspace description updated.' : 'Workspace description cleared.'
  } catch (saveError) {
    metadataError.value = `Failed to update description: ${saveError.message}`
    logger.warn('Failed to update workspace description', saveError)
  } finally {
    metadataBusy.value = false
  }
}

function toggleTokenRefresh() {
  showTokenRefresh.value = !showTokenRefresh.value
  if (!showTokenRefresh.value) {
    refreshPatValue.value = ''
    refreshSshKeyValue.value = ''
    refreshOauthTokenValue.value = ''
  }
  resetRefreshState()
}

async function refreshCredentials() {
  if (refreshBusy.value) return
  const credentials = {}
  if (refreshPatValue.value.trim()) credentials.github_token = refreshPatValue.value.trim()
  if (refreshSshKeyValue.value.trim()) credentials.ssh_private_key = refreshSshKeyValue.value
  if (refreshOauthTokenValue.value.trim()) credentials.github_oauth_token = refreshOauthTokenValue.value.trim()
  if (Object.keys(credentials).length === 0) {
    refreshError.value = 'Enter at least one credential value.'
    return
  }
  refreshBusy.value = true
  resetRefreshState()
  try {
    await patchWorkspace({ credentials })
    refreshPatValue.value = ''
    refreshSshKeyValue.value = ''
    refreshOauthTokenValue.value = ''
    showTokenRefresh.value = false
    // Bead-paused, delivery-paused, and synthesis-paused runs all auto-resume
    // now that the listener handles EXECUTING/DELIVERING/SYNTHESIZING with a
    // non-destructive patch (server/src/services/WorkerService.ts;
    // resumeWaitingCredentialsJobsForWorkspace — ADR-036 B1 added DELIVERING
    // to the git-credential-fixable resumePhase set). Planning-paused runs
    // remain stuck (planner uses Claude exclusively today; workspace
    // credential PATCH only fixes git auth), but those are rare and
    // their pause cause is `claude_auth`, distinct from the git auth
    // failures the credentials form recovers from.
    refreshSuccess.value = 'Credentials saved. Bead-paused, delivery-paused, and synthesis-paused runs on this workspace will resume automatically.'
    void fetchJobs()
  } catch (refreshErr) {
    refreshError.value = `Failed to update credentials: ${refreshErr.message}`
    logger.warn('Failed to update workspace credentials', refreshErr)
  } finally {
    refreshBusy.value = false
  }
}

async function fetchWorkspace() {
  const previousStatus = workspace.value.status
  const data = await requestJson(`/workspaces/${workspaceId}`)
  applyWorkspace(data)
  return {
    previousStatus,
    currentStatus: workspace.value.status,
  }
}

async function pollWorkspaceStatus() {
  pollBusy.value = true
  try {
    const { previousStatus, currentStatus } = await fetchWorkspace()
    pollErrorCount = 0

    if (isPendingWorkspaceStatus(currentStatus)) {
      scheduleWorkspacePoll()
      return
    }

    stopWorkspacePolling()

    if (previousStatus !== currentStatus && currentStatus === 'ready') {
      void fetchSecondaryData()
    }
  } catch (pollError) {
    pollErrorCount += 1
    logger.warn('Failed to poll workspace status', pollError)

    if (pollErrorCount >= MAX_POLL_ERRORS) {
      addWarning('Workspace status could not be refreshed automatically.')
      return
    }

    scheduleWorkspacePoll()
  } finally {
    pollBusy.value = false
  }
}

async function fetchJobs() {
  jobsLoading.value = true
  const items = await fetchAllJobSummaryItems(requestJson, {
    workspaceId,
  })
  jobs.value = items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
  jobsLoading.value = false
}

async function fetchAnalytics() {
  analyticsLoading.value = true
  const data = await requestJson(`/jobs/analytics?workspaceId=${encodeURIComponent(workspaceId)}&limit=20`)
  analytics.value = data.analytics ?? null
  analyticsLoading.value = false
}

async function fetchStaleness() {
  stalenessLoading.value = true
  try {
    const result = await requestJson(`/workspaces/${workspaceId}/staleness?mode=cached`)
    staleness.value = result.stale ? result.staleness : null

    if (result.cache?.source === 'none' || result.cache?.fresh === false) {
      void refreshStaleness()
    }
  } catch (fetchError) {
    staleness.value = null
    addWarning('Staleness information is not available for this workspace.')
    logger.warn('Failed to fetch workspace staleness', fetchError)
  } finally {
    stalenessLoading.value = false
  }
}

async function refreshStaleness() {
  try {
    const refreshed = await requestJson(`/workspaces/${workspaceId}/staleness?mode=refresh`)
    staleness.value = refreshed.stale ? refreshed.staleness : null
  } catch (refreshError) {
    logger.warn('Failed to refresh workspace staleness', refreshError)
  }
}

async function fetchSecondaryData() {
  jobsLoading.value = true
  analyticsLoading.value = true
  stalenessLoading.value = true
  warnings.value = []
  const [jobsResult, analyticsResult] = await Promise.allSettled([fetchJobs(), fetchAnalytics()])
  if (jobsResult.status === 'rejected') {
    jobs.value = []
    jobsLoading.value = false
    addWarning('Runs for this workspace could not be loaded.')
    logger.warn('Failed to fetch workspace runs', jobsResult.reason)
  }
  if (analyticsResult.status === 'rejected') {
    analytics.value = null
    analyticsLoading.value = false
    addWarning('Analytics for this workspace could not be loaded.')
    logger.warn('Failed to fetch workspace analytics', analyticsResult.reason)
  }
  await fetchStaleness()
}

async function loadData({ showLoading = false } = {}) {
  stopWorkspacePolling()
  if (showLoading) loading.value = true
  error.value = ''
  warnings.value = []
  actionMessage.value = ''
  resetDeleteState()
  jobs.value = []
  analytics.value = null
  staleness.value = null
  try {
    const { currentStatus } = await fetchWorkspace()
    loading.value = false
    if (isPendingWorkspaceStatus(currentStatus)) {
      scheduleWorkspacePoll()
    }
    void fetchSecondaryData()
  } catch (workspaceError) {
    workspace.value = {}
    error.value = 'Failed to load workspace.'
    logger.warn('Failed to fetch workspace detail', workspaceError)
    loading.value = false
  }
}

async function updateWorkspace(patch, successMessage) {
  actionBusy.value = true
  actionMessage.value = ''
  try {
    await patchWorkspace(patch)
    actionMessage.value = successMessage
    await loadData()
  } catch (updateError) {
    actionMessage.value = `Action failed: ${updateError.message}`
    logger.warn('Failed to update workspace', updateError)
  } finally {
    actionBusy.value = false
  }
}

async function submitStalenessDecision(decision) {
  actionBusy.value = true
  actionMessage.value = ''
  try {
    const response = await request(`/workspaces/${workspaceId}/staleness/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        decision,
        ...(staleApprovalJobId.value ? { jobId: staleApprovalJobId.value } : {}),
      }),
    })
    const result = await appWorkspaceTaskClient.resolveTaskResponse({ request, workspaceId, response })
    if (decision === 'new-workspace' && result.newWorkspaceId) {
      router.push({ name: 'ConsoleWorkspaceDetail', params: { id: result.newWorkspaceId } })
      return
    }
    if (result?.approvalResult && staleApprovalJobId.value) {
      actionMessage.value = result.message || 'Plan approved.'
      await router.push({ name: 'ConsoleJobDetail', params: { id: staleApprovalJobId.value } })
      return
    }
    actionMessage.value = result.message || (decision === 'sync' ? 'Workspace sync requested.' : 'Fresh workspace creation requested.')
    await loadData()
  } catch (decisionError) {
    actionMessage.value = `Action failed: ${decisionError.message}`
    logger.warn('Failed to submit staleness decision', decisionError)
  } finally {
    actionBusy.value = false
  }
}

async function prepareDeleteWorkspace() {
  actionBusy.value = true
  actionMessage.value = ''
  deleteImpactError.value = ''
  deleteCompletedWithWarnings.value = false
  try {
    deleteImpact.value = await requestJson(`/workspaces/${workspaceId}/delete-impact`)
    deleteConfirmOpen.value = true
  } catch (deleteImpactFetchError) {
    deleteImpactError.value = `Delete review failed: ${deleteImpactFetchError.message}`
    actionMessage.value = deleteImpactError.value
    logger.warn('Failed to load workspace delete impact', deleteImpactFetchError)
  } finally {
    actionBusy.value = false
  }
}

function cancelDeleteWorkspace() {
  resetDeleteState()
  actionMessage.value = ''
}

async function confirmDeleteWorkspace() {
  actionBusy.value = true
  actionMessage.value = ''
  deleteImpactError.value = ''
  try {
    const response = await request(`/workspaces/${workspaceId}`, { method: 'DELETE' })
    const result = await appWorkspaceTaskClient.resolveTaskResponse({ request, workspaceId, response })
    const cleanupWarnings = Array.isArray(result?.branchCleanup?.warnings) ? result.branchCleanup.warnings : []
    if (cleanupWarnings.length) {
      cleanupWarnings.forEach(addWarning)
      deleteConfirmOpen.value = false
      deleteCompletedWithWarnings.value = true
      actionMessage.value = 'Workspace deleted, but remote branch cleanup reported warnings.'
      return
    }
    await router.push({ name: 'ConsoleWorkspaces' })
  } catch (deleteError) {
    actionMessage.value = `Delete failed: ${deleteError.message}`
    logger.warn('Failed to delete workspace', deleteError)
  } finally {
    actionBusy.value = false
  }
}

onMounted(() => {
  // Auto-open the token-refresh form when the operator arrives via the
  // paused-job credential CTA (?refreshCredentials=1). The Administration
  // <details> opens automatically because its :open binding reads
  // showTokenRefresh, so the form is visible without an extra click.
  if (route.query.refreshCredentials === '1') {
    showTokenRefresh.value = true
  }
  loadData({ showLoading: true })
})

onUnmounted(() => {
  stopWorkspacePolling()
})
</script>

<style scoped>
.workspace-detail__stat {
  margin-left: 12px;
  color: var(--shell-text-muted);
}

.workspace-detail__warnings {
  margin-bottom: 16px;
}

.workspace-detail__action-block {
  padding: 20px 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.04) 100%);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.workspace-detail__action-block p {
  margin: 0 0 16px;
  font-size: 0.95rem;
  color: var(--shell-text);
}

.workspace-detail__action-note {
  max-width: 68ch;
  color: var(--shell-text-muted);
}

.workspace-detail__action-block strong {
  color: #b45309;
}

.workspace-detail__overview {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: start;
}

.workspace-detail__description {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--shell-text);
}

.workspace-detail__description--placeholder,
.workspace-detail__empty,
.workspace-detail__admin-value--placeholder {
  font-style: italic;
  color: var(--shell-text-muted);
}

.workspace-detail__repo {
  margin: 12px 0 0;
  font-family: var(--shell-mono-font);
  font-size: 0.8rem;
  color: var(--shell-text-muted);
}

.workspace-detail__metrics-grid {
  margin-top: 16px;
}

.workspace-detail__admin-grid,
.workspace-detail__topic-list {
  display: grid;
  gap: 0;
}

.workspace-detail__admin-block,
.workspace-detail__topic {
  padding: 16px 0;
  border-top: 1px solid var(--shell-border);
}

.workspace-detail__admin-block:first-child,
.workspace-detail__topic:first-child {
  padding-top: 0;
  border-top: 0;
}

.workspace-detail__topic:last-child {
  padding-bottom: 0;
}

.workspace-detail__admin-header,
.workspace-detail__topic-header,
.workspace-detail__actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.workspace-detail__admin-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--shell-text);
}

.workspace-detail__admin-note,
.workspace-detail__message,
.workspace-detail__admin-message,
.workspace-detail__admin-error,
.workspace-detail__view-all {
  font-size: 0.84rem;
}

.workspace-detail__admin-note {
  margin: 6px 0 0;
  line-height: 1.5;
  color: var(--shell-text-muted);
}

.workspace-detail__admin-value {
  margin: 12px 0 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--shell-text);
}

.workspace-detail__admin-form {
  display: grid;
  gap: 12px;
  margin-top: 12px;
}

.workspace-detail__input,
.workspace-detail__textarea {
  width: 100%;
}

.workspace-detail__textarea {
  resize: vertical;
  min-height: 120px;
}

.workspace-detail__input--mono,
.workspace-detail__mono {
  font-family: var(--shell-mono-font);
}

.workspace-detail__gap-tags,
.workspace-detail__topic-types {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.workspace-detail__gap-tags {
  margin-bottom: 16px;
}

.workspace-detail__topic-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--shell-text);
}

.workspace-detail__topic-count {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--shell-accent);
}

.workspace-detail__topic-types {
  margin-top: 6px;
}

.workspace-detail__meta-list {
  display: grid;
  gap: 6px;
  margin: 0;
  font-size: 0.82rem;
}

.workspace-detail__meta-list div {
  display: flex;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid var(--shell-border);
}

.workspace-detail__meta-list div:last-child {
  border-bottom: none;
}

.workspace-detail__meta-list dt {
  min-width: 140px;
  font-family: var(--shell-mono-font);
  font-size: 0.72rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--shell-text-muted);
}

.workspace-detail__meta-list dd {
  margin: 0;
  color: var(--shell-text);
}

.workspace-detail__source-block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--shell-border);
}

.workspace-detail__source-heading {
  margin: 0 0 10px;
  font-family: var(--shell-mono-font);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--shell-text-muted);
}

.workspace-detail__source-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding-left: 18px;
}

.workspace-detail__actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--shell-border);
}

.workspace-detail__delete {
  border-color: rgba(156, 48, 48, 0.24);
  color: #9c3030;
  font-size: 0.75rem;
}

.workspace-detail__delete-confirm {
  display: grid;
  gap: 14px;
  margin-top: 16px;
  padding: 16px;
  border: 1px solid rgba(156, 48, 48, 0.26);
  border-radius: 10px;
  background: rgba(156, 48, 48, 0.06);
}

.workspace-detail__delete-confirm p,
.workspace-detail__delete-title {
  margin: 0;
}

.workspace-detail__delete-title {
  font-size: 0.95rem;
  color: #9c3030;
}

.workspace-detail__delete-branch-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding-left: 18px;
}

.workspace-detail__delete-branch-list li {
  display: grid;
  gap: 2px;
}

.workspace-detail__delete-source,
.workspace-detail__delete-warnings {
  font-size: 0.82rem;
  color: var(--shell-text-muted);
}

.workspace-detail__delete-warnings {
  display: grid;
  gap: 6px;
}

.workspace-detail__view-all,
.workspace-detail__message,
.workspace-detail__admin-message {
  display: inline-block;
  margin-top: 12px;
  color: var(--shell-text-muted);
}

.workspace-detail__admin-error {
  margin-top: 12px;
  color: var(--status-failed);
}

@media (max-width: 768px) {
  .workspace-detail__overview {
    grid-template-columns: 1fr;
  }

  .workspace-detail__admin-header,
  .workspace-detail__actions {
    flex-direction: column;
  }
}
</style>
